import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import critLogo from "../assets/icons/critLogo.png";
import Sidebar from "./Sidebar";
import appConfig, { getPortalKey } from "../config";

const Header = () => {
    // Get config immediately using useMemo
    const config = useMemo(() => {
        const key = getPortalKey();
        return appConfig[key] || appConfig.portal;
    }, []);

    const [appName, setAppName] = useState(config.name);
    const [logo, setLogo] = useState(config.name === "Suquamish" ? config.whitelogo : config.logo);
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const smallScreenDivRef = useRef(null);
    const sidebarRef = useRef(null);
    const [smallScreenDivHeight, setSmallScreenDivHeight] = useState(0);
    const [ProgramTitle, setProgramTitle] = useState("both"); // Default to member login


    useEffect(() => {
        // Get login options from portal settings
        const settings = JSON.parse(sessionStorage.getItem("portalSettings"));
        const options = settings?.ProgramTitle ? `${settings.ProgramTitle} Portal` : "";
        setProgramTitle(options);

    }, []);

    const handleLogout = () => {
        // Clear all auth-related session data so no component shows stale user state
        sessionStorage.removeItem("isAuthenticated");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("userId");
        sessionStorage.removeItem("userTaxpayers");
        sessionStorage.removeItem("accountType");
        sessionStorage.removeItem("fullname");
        sessionStorage.removeItem("menuData");
        localStorage.removeItem("selectedTaxPayer");
        localStorage.removeItem("selectedBusinessName");

        // Full page redirect so the app remounts and re-reads auth state (avoids stale UI)
        const base = sessionStorage.getItem("portalBase") || "/portal";
        window.location.href = `${base}/`;
    };

    const handleHomeClick = () => navigate("/home");
    const toggleMenu = () => setIsMenuOpen((prev) => !prev);
    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    useEffect(() => {
        const updateHeight = () => {
            if (smallScreenDivRef.current) {
                setSmallScreenDivHeight(smallScreenDivRef.current.clientHeight);
            }
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setIsSidebarOpen(false);
            }
        };

        if (isSidebarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isSidebarOpen]);

    return (
        <header className="bg-primary text-white px-6 py-4 flex flex-row lg:justify-between lg:items-center">
            {/* Small Screens: Adjusted Layout */}
            <div ref={smallScreenDivRef} className="flex w-full items-center justify-between lg:hidden">
                <div className="flex flex-col items-start">
                    <img src={logo} alt="Logo" className={`${appName === "Colorado River Indian Tribes" ? "h-20" : "h-8"} w-auto object-contain`} />
                    <button className="text-2xl mt-5 ml-4" onClick={toggleSidebar}>
                        {isSidebarOpen ? (
                            <FaTimes className="w-5 h-5" />
                        ) : (
                            <FaBars className="w-5 h-5" />
                        )}
                    </button>
                </div>
                <div className="flex flex-col items-end">
                    <button
                        onClick={handleHomeClick}
                        className="text-lg font-bold leading-tight text-right hover:underline"
                    >
                        {ProgramTitle}
                    </button>
                    <button type="button" onClick={handleLogout} className="mt-2 hover:underline text-lg">Logout</button>
                </div>
            </div>

            {/* Large Screens: Navigation */}
            <div className="hidden lg:flex flex-col lg:items-start">
                <img src={logo} alt="Logo" className={`${appName === "Colorado River Indian Tribes" ? "h-20" : "h-10"} w-auto object-contain`} />
            </div>

            {/* Large Screens: Title & Logout */}
            <div className="hidden lg:flex flex-col items-end">
                <button
                    onClick={handleHomeClick}
                    className="text-lg lg:text-xl font-bold leading-tight text-right hover:underline"
                >
                    {ProgramTitle}
                </button>
                <button type="button" onClick={handleLogout} className="mt-2 hover:underline text-lg">Logout</button>
            </div>

            {/* Sidebar with dynamic top margin */}
            {isSidebarOpen && (
                <div
                    ref={sidebarRef}
                    className={`fixed left-0 z-50 transition-transform duration-300 ease-in-out transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} shadow-lg`}
                    style={
                        // Only add extra top margin for CRIT on large screens
                        appName === "Colorado River Indian Tribes" && window.innerWidth >= 1024
                            ? { top: "112px" } // h-28 (7rem = 112px), adjust if needed
                            : { top: "64px" }  // h-16 (4rem = 64px), or your default
                    }
                >
                    <Sidebar onItemSelect={() => setIsSidebarOpen(false)} />
                </div>
            )}
        </header>
    );
};

export default Header;